<template>
    <div class="main-container px-2 md:px-16">
        <Toast />
        <ConfirmPopup></ConfirmPopup>

        <div class="grid grid-cols-12 gap-6">
            <!-- First row -->
            <div class="col-span-12">
                <div class="flex flex-col sm:flex-row gap-6 sm:items-center sm:justify-between mt-5">
                    <div class="flex flex-col items-center sm:items-start w-full sm:w-auto" v-if="user">
                        <Breadcrumb :model="breadcrumbItems" class="mb-2">
                            <template #item="{ item, props }">
                                <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
                                    <a :href="href" v-bind="props.action" @click="navigate">
                                        {{ item.label }}
                                    </a>
                                </router-link>
                                <a v-else :href="item.url" :target="item.target" v-bind="props.action">
                                    {{ item.label }}
                                </a>
                            </template>
                        </Breadcrumb>
                        <div class="flex items-center gap-3">
                            <span class="text-900 font-bold text-2xl sm:text-4xl text-center sm:text-left">
                                {{ user.type === 'Company' ? user.companyName : user.name }}
                            </span>
                            <i 
                                v-if="!user.termsSigned" 
                                class="pi pi-exclamation-triangle text-red-500 cursor-pointer hover:text-red-700" 
                                v-tooltip.bottom="'No terms signed'"
                                @click="handleTermsClick"
                            />
                                <i 
                                    v-else 
                                    class="pi pi-check-circle text-green-500 cursor-pointer hover:text-green-700" 
                                    v-tooltip.bottom="'Terms signed'"
                                    @click="handleTermsClick"
                                />
                                <TermsDialog
                                    v-model:visible="termsDialogVisible"
                                    :url="termsUrl"
                                />
                        </div>
                        <p class="text-600 m-0 text-sm sm:text-base text-center sm:text-left">
                            Last logged in on {{ getFormattedLoggedInDate(user.lastLogin) }}
                        </p>
                    </div>
                    <div class="flex flex-col items-center w-full sm:w-auto sm:items-end mt-4 sm:mt-0" v-if="user">
                        <Button :label="`Login as ${userRole}`" icon="pi pi-sign-in" severity="primary"
                            :loading="loginAsPublisherLoading" class="whitespace-nowrap" @click="loginAsPublisher" />
                    </div>
                    <div class="flex flex-col items-center sm:items-start gap-4 w-full sm:w-auto"
                        v-if="user && userRole === 'publisher'">
                        <div class="flex flex-wrap gap-2 w-full max-w-[300px] group relative">
                            <div
                                class="flex flex-wrap gap-1 max-h-[64px] overflow-hidden justify-center sm:justify-start w-full">
                                <Tag v-for="source in user.trafficSource.slice(0, 2)" :key="source"
                                    :value="siteTypeMap[source]" severity="info" class="!px-2 !py-1" />
                                <Tag v-if="user.trafficSource.length > 2" :value="`+${user.trafficSource.length - 2}`"
                                    severity="secondary" class="!px-2 !py-1 cursor-help" />
                            </div>
                            <!-- Hover popup -->
                            <div v-if="user.trafficSource.length > 2"
                                class="absolute left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 top-full mt-2 bg-white shadow-lg rounded-md p-2 hidden group-hover:flex flex-wrap gap-1 z-50">
                                <Tag v-for="source in user.trafficSource" :key="source" :value="siteTypeMap[source]"
                                    severity="info" class="!px-2 !py-1" />
                            </div>
                        </div>
                        <div class="flex flex-wrap gap-2 w-full max-w-[300px] group relative"
                            v-if="user && userRole === 'publisher'">
                            <div
                                class="flex flex-wrap gap-1 max-h-[64px] overflow-hidden justify-center sm:justify-start w-full">
                                <Tag v-for="geo in user.trafficGeo.slice(0, 2)" :key="geo" class="!px-2 !py-1">
                                    <span :class="`fi fi-${geo.toLowerCase()}`" class="mr-2"></span>
                                    {{ getCountryName(geo) }}
                                </Tag>
                                <Tag v-if="user.trafficGeo.length > 2" :value="`+${user.trafficGeo.length - 2}`"
                                    severity="secondary" class="!px-2 !py-1 cursor-help" />
                            </div>
                            <!-- Hover popup -->
                            <div v-if="user.trafficGeo.length > 2"
                                class="absolute left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 top-full mt-2 bg-white shadow-lg rounded-md p-2 hidden group-hover:flex flex-wrap gap-1 z-50">
                                <Tag v-for="geo in user.trafficGeo" :key="geo" class="!px-2 !py-1">
                                    <span :class="`fi fi-${geo.toLowerCase()}`" class="mr-2"></span>
                                    {{ getCountryName(geo) }}
                                </Tag>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-col items-center w-full sm:w-auto sm:items-end mt-4 sm:mt-0"
                        v-if="user && userRole === 'publisher'">
                        <Tag v-if="!isEditingStatus" v-tooltip.top="'Click to update user status'" :value="user.status"
                            :severity="Utils.getUserStatusSeverity(user.status)" style="border-color: #86bcf1;"
                            class="border cursor-pointer border-opacity-20 !h-16 sm:!h-20 !w-36 sm:!w-44 !text-lg sm:!text-xl"
                            @click="editStatus" />
                        <Select v-else ref="selectRef" v-model="user.status" :options="Statuses.getUserStatus()"
                            optionLabel="status" optionValue="status" @change="handleStatusChange"
                            @before-hide="stopEditing" class="w-full sm:w-auto">
                            <template #option="slotProps">
                                <Tag :value="slotProps.option.status"
                                    :severity="Utils.getUserStatusSeverity(slotProps.option.status)" />
                            </template>
                        </Select>
                    </div>
                </div>
            </div>

            <!-- Second row -->
            <div class="col-span-12 md:col-span-6 xl:col-span-4 ">
                <Card class="h-full card-bg">
                    <template #content>
                        <Skeleton v-if="user === null" width="50px" height="50px" />
                        <div v-else>
                            <p class="m-0" v-if="user.type === 'Company'"><strong>Contact:</strong> {{ user.name }}</p>
                            <p class="m-0"><strong>Email:</strong> {{ user.email }}</p>
                            <p class="m-0"><strong>Contact:</strong> {{ user.contact }}</p>
                            <div class="flex items-center space-x-2">
                                <p class="m-0"><strong>Country:</strong> {{ getCountryName(user.country) }}</p>
                                <span :class="`fi fi-${user.country.toLowerCase()}`" class="mr-2"></span>
                            </div>
                            <p class="m-0"><strong>Created:</strong> {{ user.created }}</p>
                        </div>
                    </template>
                </Card>
            </div>

            <div class="col-span-12 md:col-span-6 xl:col-span-2">
                <WalletCard :loading="user === null" :userId="props.id" />
            </div>

            <div class="col-span-12 md:col-span-6 xl:col-span-2">
                <Card class="h-full card-bg">
                    <template #title><span class="font-semibold text-lg">Commission</span> </template>
                    <template #content>
                        <div class="flex flex-1 items-center justify-center">
                            <span class="text-4xl font-bold text-surface-900 dark:text-surface-0">
                                <Skeleton v-if="user === null" width="50px" height="50px" />
                                <div v-if="user !== null && !commissionEdit">
                                    <a v-tooltip.bottom="'Click to change'" @click="showEditCommission"
                                        class="text-lg text-center  cursor-pointer">{{ user.commission }}%</a>
                                </div>
                                <div v-if="commissionEdit" class="flex items-center " fluid>
                                    <InputNumber @keyup.enter="saveCommission" v-model="user.commission"
                                        inputId="new-commission" suffix="%" :minFractionDigits="2" :step="0.25" fluid />
                                    <Button class=" cursor-pointer" :loading="commissionSaving"
                                        :disabled="!hasCommissionChanged" label="Save" icon="pi pi-check"
                                        iconPos="right" @click="saveCommission" />
                                </div>
                            </span>
                        </div>
                    </template>
                </Card>
            </div>

            <div class="col-span-12 md:col-span-6 xl:col-span-2" v-if="user && userRole === 'publisher'">
                <Card class="h-full card-bg">
                    <template #title><span class="font-semibold text-lg"> Total Links</span></template>
                    <template #content>
                        <div class="flex flex-1 items-center justify-center">
                            <span class="text-4xl font-bold text-surface-900 dark:text-surface-0">
                                <Skeleton v-if="userMetaData === null" width="50px" height="50px" />
                                <span v-else>{{ userMetaData.linkCount }}</span>
                            </span>
                        </div>
                    </template>
                </Card>
            </div>

            <div class="col-span-12 md:col-span-6 xl:col-span-2" v-if="user && userRole === 'publisher'">
                <Card class="h-full card-bg">
                    <template #title><span class="font-semibold text-lg">Total sites</span> </template>
                    <template #content>
                        <div class="flex flex-1 items-center justify-center">
                            <span class="text-4xl font-bold text-surface-900 dark:text-surface-0">
                                <Skeleton v-if="userMetaData === null" width="50px" height="50px" />
                                <span v-else>{{ userMetaData.siteCount }}</span>
                            </span>
                        </div>
                    </template>
                </Card>
            </div>

            <div class="card main-bg-color{ col-span-12 ">
                <PaymentDetails v-if="user" :user="user" />
            </div>

            <div class="col-span-12" v-if="user && userRole === 'publisher'">
                <Card class="h-full card-bg-dark border-1">
                    <template #title>
                        <div class="flex justify-between items-center">
                            <div class="text-xl font-semibold mb-4 text-white underline">Sites</div>
                            <div class="flex items-center gap-4 mr-auto ml-6">
                                <div class="flex items-center gap-2">
                                    <Button
                                        label="By site"
                                        :severity="linksViewMode === 'grouped' ? 'secondary' : 'contrast'"
                                        size="small"
                                        @click="linksViewMode = 'grouped'"
                                    />
                                    <Button
                                        label="All links"
                                        :severity="linksViewMode === 'flat' ? 'secondary' : 'contrast'"
                                        size="small"
                                        @click="linksViewMode = 'flat'"
                                    />
                                </div>
                                <Button
                                    v-if="linksViewMode !== 'flat'"
                                    v-tooltip.top="'Add new Site'"
                                    icon="pi pi-plus"
                                    rounded
                                    severity="secondary"
                                    aria-label="Add" @click="handleAddSite" />
                            </div>
                        </div>
                    </template>
                    <template #content>
                        <!-- <SelectButton v-model="showing" optionValue="value" :options="showingOptions"
                            optionLabel="label" dataKey="value" aria-labelledby="basic" /> -->
                        <div class="flex justify-center items-center mt-2">
                            <div class="w-full bg-white rounded-lg shadow-md">
                                <DataTable v-if="linksViewMode === 'grouped'" v-model:expandedRows="expandedRows" ref="dataTable" :value="sites"
                                    editMode="cell" dataKey="id" @cell-edit-complete="onUpdateSiteStatus" scrollable
                                    :scrollHeight="'70vh'" :frozenValue="frozenSites" :pt="{
                                        table: { style: 'min-width: 50rem' },
                                        bodyrow: ({ props }) => ({
                                            class: [{ 'font-bold': props.frozenRow }]
                                        })
                                    }">
                                    <Column v-if="!sitesLoading" expander :headerStyle="headerStyles" :bodyStyle="{
                                        paddingLeft: '0',
                                        paddingRight: '0'
                                    }">

                                    </Column>
                                    <Column :header="siteName" :headerStyle="headerStyles">
                                        <template v-if="sitesLoading" #body>
                                            <Skeleton></Skeleton>
                                        </template>
                                        <template v-else #body="{ data }">
                                            <a v-if="isUserSeo" class="clickable-link" :href="Utils.formatUrl(data.url)"
                                                target="_blank" rel="noopener noreferrer">
                                                {{ data.url }}
                                            </a>
                                            <span v-else>{{ data.name }}</span>
                                        </template>
                                    </Column>
                                    <Column style="width: 5rem" header="Links" :headerStyle="headerStyles">
                                        <template v-if="sitesLoading" #body>
                                            <Skeleton></Skeleton>
                                        </template>
                                        <template v-else #body="{ data }">
                                            {{ getAdvertisersBySiteId(data.id).value.length }}
                                        </template>
                                    </Column>
                                    <Column field="type" header="Type" :headerStyle="headerStyles">
                                        <template v-if="sitesLoading" #body>
                                            <Skeleton></Skeleton>
                                        </template>
                                        <template v-else #body="slotProps">
                                            <Tag :value="slotProps.data.type"
                                                :severity="Utils.getSiteTypeSeverity(slotProps.data.type)" />
                                        </template>
                                    </Column>
                                    <!-- <Column field="url" header="Domain" :headerStyle="headerStyles">
                                        <template v-if="isLoading" #body>
                                            <Skeleton></Skeleton>
                                        </template>
                                        <template v-else #body="slotProps">
                                            <a class="clickable-link" :href="Utils.formatUrl(slotProps.data.url)"
                                                target="_blank" rel="noopener noreferrer">{{ slotProps.data.url
                                                }}</a>
                                        </template>
                                    </Column> -->
                                    <Column field="country" header="Country" :headerStyle="headerStyles">
                                        <template v-if="sitesLoading" #body>
                                            <Skeleton></Skeleton>
                                        </template>
                                        <template v-else #body="slotProps">
                                            <!-- Create a router link using the data in the slotProps -->
                                            <span>{{ getCountryName(slotProps.data.country) }}</span>
                                            <span :class="`fi fi-${slotProps.data.country.toLowerCase()}`"
                                                class="ml-2"></span>
                                        </template>
                                    </Column>
                                    <Column field="createdAt" header="Created" :headerStyle="headerStyles">
                                        <template v-if="sitesLoading" #body>
                                            <Skeleton></Skeleton>
                                        </template>
                                        <template v-else #body="{ data }">
                                            {{ formatDateOnly(data.createdAt) }}
                                        </template>
                                    </Column>
                                    <Column field="status" header="Status" :headerStyle="headerStyles">
                                        <template v-if="sitesLoading" #body>
                                            <Skeleton></Skeleton>
                                        </template>
                                        <template v-else #body="slotProps">
                                            <Tag :value="`site ${slotProps.data.status}`"
                                                :severity="Utils.getSiteStatusSeverity(slotProps.data.status)" />
                                        </template>
                                        <template v-if="!isLoading" #editor="{ data, field }">
                                            <Select v-model="data[field]" :options="statuses" optionLabel="status"
                                                optionValue="status" fluid>
                                                <template #option="slotProps">
                                                    <Tag :value="slotProps.option.status"
                                                        :severity="Utils.getSiteStatusSeverity(slotProps.option.status)" />
                                                </template>
                                            </Select>
                                        </template>
                                    </Column>
                                    <Column :headerStyle="headerStyles">
                                        <template #body="{ data }">
                                            <Button v-tooltip.top="'Add new Campaign'" icon="pi pi-plus" rounded
                                                severity="secondary" aria-label="Add" @click="handleAddNewDeal(data)" />
                                        </template>
                                    </Column>
                                    <template #expansion="slotProps">
                                        <DataTable class="ml-8" v-if="anyAdvertisersBySiteId(slotProps.data.id).value"
                                            :value="getAdvertisersBySiteId(slotProps.data.id).value"
                                            :frozenValue="getFrozenAdvertisers(slotProps.data.id)"
                                            v-model:editingRows="editingRows" editMode="row"
                                            @row-edit-save="onRowEditSave" @row-edit-cancel="onRowEditCancel"
                                            :contextMenu="true" @row-contextmenu="onLinkRightClick"
                                            dataKey="linkId" :pt="{
                                                table: { style: 'min-width: 50rem' },
                                                bodyrow: ({ props }) => ({
                                                    class: [{ 'font-bold': props.frozenRow }]
                                                })
                                            }">
                                            <Column header="Advertiser" :headerStyle="nestedHeaderStyles">
                                                <template #body="{ data }">
                                                    <a v-tooltip.top="`${data.affiliateSystem.name}`" class="underline"
                                                        :href="data.affiliateSystem.loginUrl" target="_blank"
                                                        rel="noopener noreferrer">
                                                        {{ data.name }}
                                                    </a>
                                                </template>
                                            </Column>
                                            <Column field="deal" header="Pub deal" :headerStyle="nestedHeaderStyles">
                                                <template #body="slotProps">
                                                    <a v-if="dealEditHref(slotProps.data)"
                                                        :href="dealEditHref(slotProps.data)" target="_blank"
                                                        rel="noopener noreferrer" class="underline"
                                                        v-tooltip.top="'Open this deal in the advertiser editor'">
                                                        {{ getFriendlyDealMessage(slotProps.data.deal) }}
                                                    </a>
                                                    <span v-else>{{ getFriendlyDealMessage(slotProps.data.deal) }}</span>
                                                </template>
                                            </Column>
                                            <Column header="Campaign Key" :headerStyle="nestedHeaderStyles"
                                                style="width: 10rem">
                                                <template #body="{ data, field, index }">
                                                    <div v-if="editingRows.includes(index)" class="flex flex-col">
                                                        <InputText v-model="data.internalNote" fluid
                                                            placeholder="Add campaign key" />
                                                    </div>
                                                    <div v-else>
                                                        <div class="whitespace-normal break-words"
                                                            :class="{ 'text-red-500': !data.internalNote }">
                                                            {{ data.internalNote || 'No key added' }}
                                                        </div>
                                                    </div>
                                                </template>
                                            </Column>
                                            <Column field="affiliateLink" header="Tracking link"
                                                :headerStyle="nestedHeaderStyles" style="width: 40rem">
                                                <template #body="{ data, field, index }">
                                                    <div v-if="editingRows.includes(index)" class="flex flex-col">
                                                        <InputText v-model="data[field]" fluid
                                                            placeholder="Enter affiliate link"
                                                            @input="handleAffiliateLinkInput(data)"
                                                            :class="{ 'p-invalid': !isValidAffiliateLink(data.affiliateLink, data.internalNote, data.affiliateSystem.name) && data.affiliateLink }" />
                                                        <small class="p-error"
                                                            v-if="!isValidAffiliateLink(data.affiliateLink, data.internalNote, data.affiliateSystem.name)">
                                                            {{ expectedAffiliateLinkFormat(data.affiliateSystem.name,
                                                                data.internalNote) }}
                                                            <Button v-if="getLinkParamsText(data.affiliateSystem?.name, data.internalNote)"
                                                                icon="pi pi-copy" severity="info" class="p-button-text p-button-sm"
                                                                v-tooltip.bottom="'Copy link parameters'"
                                                                @click="copyLinkParams(data.affiliateSystem?.name, data.internalNote)" />
                                                        </small>
                                                    </div>
                                                    <div class="flex" v-else-if="data.affiliateLink">
                                                        <div class="aff-link cursor-pointer flex items-center gap-2">
                                                            <a :href="Utils.formatUrl(data.affiliateLink)"
                                                                target="_blank" rel="noopener noreferrer"
                                                                class="hover:underline aff-link">
                                                                {{ data.affiliateLink }}
                                                            </a>
                                                            <Button icon="pi pi-copy" text rounded size="small"
                                                                class="aff-link" v-tooltip.bottom="'Copy link'"
                                                                @click.stop="copyToClipboard(data.affiliateLink)" />
                                                        </div>
                                                    </div>
                                                </template>
                                            </Column>
                                            <Column field="createdAt" header="RequestedAt"
                                                :headerStyle="nestedHeaderStyles">
                                                <template #body="{ data }">
                                                    {{ formatDateOnly(data.createdAt) }}
                                                </template>
                                            </Column>
                                            <Column header="Mapped" :headerStyle="nestedHeaderStyles">
                                                <template #body="{ data }">
                                                    {{ data.mapped }}
                                                </template>
                                            </Column>
                                            <Column field="status" header="Link status"
                                                :headerStyle="nestedHeaderStyles">
                                                <template #body="slotProps">
                                                    <Tag :value="slotProps.data.status"
                                                        :severity="Utils.getLinkStatusSeverity(slotProps.data.status)" />
                                                </template>
                                            </Column>

                                            <Column style="width: 10%; min-width: 8rem" bodyStyle="text-align:center"
                                                :headerStyle="nestedHeaderStyles">
                                                <template #body="{ data, index }">
                                                    <div v-if="editingRows.includes(index)">
                                                        <Button icon="pi pi-check"
                                                            class="p-button-text p-button-success"
                                                            @click="saveEdit(data, index)" />
                                                        <Button icon="pi pi-times" class="p-button-text p-button-danger"
                                                            @click="cancelEdit(index)" />
                                                    </div>
                                                    <div v-else>
                                                        <Button 
                                                            icon="pi pi-trash" 
                                                            class="p-button-text p-button-danger"
                                                            @click="confirmDeleteSiteBrandLink($event, data, index)" 
                                                        />
                                                        <Button icon="pi pi-question-circle" class="p-button-text p-button-help"
                                                            v-tooltip.left="helpButtonTooltip(data.affiliateSystem?.name)"
                                                            @click="openHelpForSystem(data.affiliateSystem?.name)" />
                                                        <Button icon="pi pi-pencil" class="p-button-text"
                                                            @click="editRow(index)" />
                                                    </div>
                                                </template>
                                            </Column>
                                        </DataTable>
                                        <div class="flex items-center justify-center h-full" v-else>
                                            <h5> Linked Advertisers will appear here</h5>
                                        </div>
                                    </template>
                                </DataTable>
                                <DataTable
                                    v-else
                                    :value="allAdvertisers"
                                    v-model:editingRows="editingRows"
                                    editMode="row"
                                    @row-edit-save="onRowEditSave"
                                    @row-edit-cancel="onRowEditCancel"
                                    :contextMenu="true"
                                    @row-contextmenu="onLinkRightClick"
                                    dataKey="linkId"
                                    scrollable
                                    :scrollHeight="'70vh'"
                                    :pt="{ table: { style: 'min-width: 65rem' } }"
                                >
                                    <Column :header="siteName" :headerStyle="nestedHeaderStyles">
                                        <template #body="{ data }">
                                            <a
                                                v-if="isUserSeo"
                                                class="underline"
                                                :href="Utils.formatUrl(data.siteUrl)"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {{ data.siteUrl }}
                                            </a>
                                            <span v-else>{{ data.siteName }}</span>
                                        </template>
                                    </Column>
                                    <Column header="Advertiser" :headerStyle="nestedHeaderStyles">
                                        <template #body="{ data }">
                                            <a v-tooltip.top="`${data.affiliateSystem.name}`" class="underline"
                                                :href="data.affiliateSystem.loginUrl" target="_blank"
                                                rel="noopener noreferrer">
                                                {{ data.name }}
                                            </a>
                                        </template>
                                    </Column>
                                    <Column field="deal" header="Pub deal" :headerStyle="nestedHeaderStyles">
                                        <template #body="slotProps">
                                            <a v-if="dealEditHref(slotProps.data)"
                                                :href="dealEditHref(slotProps.data)" target="_blank"
                                                rel="noopener noreferrer" class="underline"
                                                v-tooltip.top="'Open this deal in the advertiser editor'">
                                                {{ getFriendlyDealMessage(slotProps.data.deal) }}
                                            </a>
                                            <span v-else>{{ getFriendlyDealMessage(slotProps.data.deal) }}</span>
                                        </template>
                                    </Column>
                                    <Column header="Market" :headerStyle="nestedHeaderStyles" style="width: 6rem">
                                        <template #body="{ data }">
                                            <span v-if="data.market" v-tooltip.top="data.market"
                                                :class="`fi fi-${data.market.toLowerCase()}`" />
                                        </template>
                                    </Column>
                                    <Column header="Campaign Key" :headerStyle="nestedHeaderStyles" style="width: 10rem">
                                        <template #body="{ data, index }">
                                            <div v-if="editingRows.includes(index)" class="flex flex-col">
                                                <InputText v-model="data.internalNote" fluid placeholder="Add campaign key" />
                                            </div>
                                            <div v-else>
                                                <div class="whitespace-normal break-words"
                                                    :class="{ 'text-red-500': !data.internalNote }">
                                                    {{ data.internalNote || 'No key added' }}
                                                </div>
                                            </div>
                                        </template>
                                    </Column>
                                    <Column field="affiliateLink" header="Tracking link" :headerStyle="nestedHeaderStyles" style="width: 40rem">
                                        <template #body="{ data, field, index }">
                                            <div v-if="editingRows.includes(index)" class="flex flex-col">
                                                <InputText
                                                    v-model="data[field]"
                                                    fluid
                                                    placeholder="Enter affiliate link"
                                                    @input="handleAffiliateLinkInput(data)"
                                                    :class="{ 'p-invalid': !isValidAffiliateLink(data.affiliateLink, data.internalNote, data.affiliateSystem.name) && data.affiliateLink }"
                                                />
                                                <small
                                                    class="p-error"
                                                    v-if="!isValidAffiliateLink(data.affiliateLink, data.internalNote, data.affiliateSystem.name)"
                                                >
                                                    {{ expectedAffiliateLinkFormat(data.affiliateSystem.name, data.internalNote) }}
                                                    <Button v-if="getLinkParamsText(data.affiliateSystem?.name, data.internalNote)"
                                                        icon="pi pi-copy" severity="info" class="p-button-text p-button-sm"
                                                        v-tooltip.bottom="'Copy link parameters'"
                                                        @click="copyLinkParams(data.affiliateSystem?.name, data.internalNote)" />
                                                </small>
                                            </div>
                                            <div class="flex" v-else-if="data.affiliateLink">
                                                <div class="aff-link cursor-pointer flex items-center gap-2">
                                                    <a :href="Utils.formatUrl(data.affiliateLink)"
                                                        target="_blank" rel="noopener noreferrer"
                                                        class="hover:underline aff-link">
                                                        {{ data.affiliateLink }}
                                                    </a>
                                                    <Button icon="pi pi-copy" text rounded size="small"
                                                        class="aff-link" v-tooltip.bottom="'Copy link'"
                                                        @click.stop="copyToClipboard(data.affiliateLink)" />
                                                </div>
                                            </div>
                                        </template>
                                    </Column>
                                    <Column field="createdAt" header="RequestedAt" :headerStyle="nestedHeaderStyles">
                                        <template #body="{ data }">
                                            {{ formatDateOnly(data.createdAt) }}
                                        </template>
                                    </Column>
                                    <Column header="Mapped" :headerStyle="nestedHeaderStyles">
                                        <template #body="{ data }">
                                            {{ data.mapped }}
                                        </template>
                                    </Column>
                                    <Column field="status" header="Link status" :headerStyle="nestedHeaderStyles">
                                        <template #body="slotProps">
                                            <Tag :value="slotProps.data.status"
                                                :severity="Utils.getLinkStatusSeverity(slotProps.data.status)" />
                                        </template>
                                    </Column>
                                    <Column style="width: 10%; min-width: 8rem" bodyStyle="text-align:center"
                                        :headerStyle="nestedHeaderStyles">
                                        <template #body="{ data, index }">
                                            <div v-if="editingRows.includes(index)">
                                                <Button icon="pi pi-check" class="p-button-text p-button-success"
                                                    @click="saveEdit(data, index)" />
                                                <Button icon="pi pi-times" class="p-button-text p-button-danger"
                                                    @click="cancelEdit(index)" />
                                            </div>
                                            <div v-else>
                                                <Button
                                                    icon="pi pi-trash"
                                                    class="p-button-text p-button-danger"
                                                    @click="confirmDeleteSiteBrandLink($event, data, index)"
                                                />
                                                <Button icon="pi pi-question-circle" class="p-button-text p-button-help"
                                                    v-tooltip.left="helpButtonTooltip(data.affiliateSystem?.name)"
                                                    @click="openHelpForSystem(data.affiliateSystem?.name)" />
                                                <Button icon="pi pi-pencil" class="p-button-text"
                                                    @click="editRow(index)" />
                                            </div>
                                        </template>
                                    </Column>
                                </DataTable>
                            </div>
                        </div>
                    </template>
                </Card>
            </div>
        </div>
        <AddSite ref="addSiteRef" :key="user?.type" :isSeo="isUserSeo" :sites="sites" :userId="props.id"
            @site-added="handleSiteAdded" />
        <AddDealDialog ref="addDealRef" :key="selectedSite?.id || 'no-site'" :userId="props.id" :site="selectedSite"
            @save="handleDealSaveAndAutomateLinkRequest" />

        <!-- Right-click menu on any site-link row -->
        <ContextMenu ref="linkContextMenu" :model="linkContextMenuItems" />
        <DuplicateCampaignKeyDialog v-model:visible="showDuplicateKeyDialog" :details="duplicateKeyDetails" />

        <!-- Move-to-another-site dialog. Targets come from the backend and are
             restricted to this publisher's own approved sites. -->
        <Dialog v-model:visible="showMoveDialog" modal header="Move link to a different site"
            class="w-full md:w-[34rem]" dismissableMask closeOnEscape>
            <div v-if="linkToMove" class="flex flex-col gap-4">
                <div class="text-sm">
                    <p class="mb-1">
                        Moving <strong>{{ linkToMove.name }}</strong>
                        <span v-if="linkToMove.market"> ({{ linkToMove.market }})</span>
                    </p>
                    <p class="text-gray-400">
                        Currently on <strong>{{ linkToMove.siteName || currentSiteNameForMove }}</strong>
                    </p>
                </div>

                <div v-if="moveTargetsLoading" class="text-sm text-gray-400">Loading available sites…</div>

                <div v-else-if="!moveTargets.length"
                    class="text-sm p-3 rounded border border-amber-400/50 bg-amber-400/5">
                    This publisher has no other approved sites to move the link to.
                </div>

                <div v-else class="flex flex-col gap-2">
                    <label class="text-sm font-medium">Move to</label>
                    <Select v-model="selectedMoveTargetId" :options="moveTargets" optionLabel="name"
                        optionValue="_id" placeholder="Choose a site" class="w-full">
                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <span>{{ slotProps.option.name }}</span>
                                <Tag :value="slotProps.option.type" severity="secondary" class="!text-xs" />
                            </div>
                        </template>
                    </Select>
                    <small class="text-gray-500">
                        Only this publisher's approved sites are listed — links can't move between publishers.
                    </small>
                </div>
            </div>

            <template #footer>
                <Button label="Cancel" text @click="showMoveDialog = false" />
                <Button label="Move" icon="pi pi-arrow-right" severity="primary"
                    :disabled="!selectedMoveTargetId || movingLink" :loading="movingLink"
                    @click="confirmMoveLink" />
            </template>
        </Dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import UsersService from '@/services/UsersService';
import AuthService from '@/services/AuthService';
import SitesService from '@/services/SitesService';
import SiteLinkService from '@/services/SiteLinkService';
import EarningsService from '@/services/EarningsService';
import PaymentDetails from './PaymentDetails.vue';
import { getCountryName } from '@/utils/countries';
import { headerStyles, nestedHeaderStyles } from '@/global/headerStyles';
import Utils from '@/utils';
import { useToast } from "primevue/usetoast";
import { _ } from 'core-js';
import Statuses from '@/utils/statuses';
import Validation from '@/utils/validation';
import { getFormattedLoggedInDate, formatDateOnly } from '@/utils/dateHelper';
import { isValidAffiliateLink, expectedAffiliateLinkFormat } from '@/utils/tracking-link-validator';
import { enrichAffiliateLink } from '@/utils/affiliate-link-enricher';
import { helpButtonTooltip, helpQueryForSystem, getLinkParamsText, isCampaignKeyTaken } from '@/utils/affiliate-system-help';
import DuplicateCampaignKeyDialog from '@/components/DuplicateCampaignKeyDialog.vue';
import { getFriendlyDealMessage } from '@/utils/deals';
import { getSiteTypes } from '@/utils/siteTypes';
import { getRoleName } from '@/utils/roles';
import WalletCard from './UserWallet.vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import AddSite from '@/components/AddSite.vue';
import AddDealDialog from '@/components/AddDealDialog.vue';
import AdvertiserService from '@/services/AdvertiserService';
import TermsService from '@/services/TermsService';
import TermsDialog from './TermsDialog.vue';
import { useConfirm } from "primevue/useconfirm";
import ConfirmPopup from 'primevue/confirmpopup';
import ContextMenu from 'primevue/contextmenu';

const store = useStore();
const router = useRouter();

/**
 * Deep link from a publisher's deal straight into the advertiser editor,
 * scrolled to that exact deal. A market can hold several deals with identical
 * terms, so we pass the dealId rather than relying on the displayed rate.
 * Returns null when the row lacks the ids, so the cell falls back to plain text.
 */
function dealEditHref(row) {
    // The admin view gets advertiserId populated as { _id, name }; other paths send the bare id.
    const advertiserId = row?.advertiserId?._id ?? row?.advertiserId;
    if (!advertiserId || !row.dealId) return null;
    return router.resolve({
        path: '/advertisers',
        query: { showing: 'settings', open: String(advertiserId), deal: String(row.dealId) },
    }).href;
}

const siteName = computed(() => store.getters.isSeo ? 'Site' : 'Toplist');

const domainPattern = Validation.getDomainPattern();
//commission changes
const commissionEdit = ref(false);
const originalCommission = ref(0);
const commissionSaving = ref(false);
const hasCommissionChanged = computed(() => {
    return user.value.commission !== originalCommission.value;
});

const showEditCommission = () => commissionEdit.value = !commissionEdit.value;
const toast = useToast();
const route = useRoute();
const props = defineProps({
    id: {
        type: String,
        required: true
    }
});


const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.add({
            severity: 'success',
            summary: 'Copied!',
            detail: 'Link copied to clipboard',
            life: 3000
        });
    } catch (err) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to copy to clipboard',
            life: 3000
        });
    }
};

const saveCommission = async () => {
    let commission = user.value.commission;
    if (commission === '' || isNaN(commission) || commission < 0) {
        commissionSaving.value = true;
        toast.add({
            severity: 'warn',
            summary: 'Invalid Input',
            detail: 'Please enter a valid commission value.',
            life: 3000
        });
        return;
    }

    try {
        const response = await UsersService.updateUserCommission(user.value.id, commission);
        originalCommission.value = response.data.commission;
        commissionSaving.value = false;
        commissionEdit.value = false;
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'User commission updated successfully!',
            life: 3000
        });
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update commission.',
            life: 3000
        });
        console.error('Error updating commission:', error);
    }
};

const selectRef = ref(null);
const isEditingStatus = ref(false);
const sites = ref(new Array(10));
const frozenSites = ref([]);
const frozenAdvertisers = ref({});
const linksViewMode = ref('grouped');
const expandedRows = ref({});
const editingRows = ref([]);
const user = ref(null);
const userMetaData = ref(null);

const userRole = computed(() => getRoleName(user.value?.roles));

// Dynamically compute breadcrumb items
const breadcrumbItems = computed(() => [
    { label: 'Users', route: '/users' }, // Link to the Users page
    { label: user.value ? user.value.id : 'Loading...' } // Display userId once available
]);
const statuses = ref([{ status: 'pending approval' }, { status: 'approved' }, { status: 'rejected' }]);

const isLoading = ref(true);
const sitesLoading = ref(true);

const showing = ref('all');
const showingOptions = ref([{ label: 'Show Only Requested', value: 'requested' }, { label: 'Show All', value: 'all' }]);

// Add a reactive variable for terms URL
const termsDialogVisible = ref(false);
const termsUrl = ref('');

const handleTermsClick = async () => {
    console.log('Terms clicked for user:', user.value.id);
    const response = await TermsService.getUserTermsUrl(user.value.id);
    console.log('Terms URL:', response.data);
    termsUrl.value = response.data; // Store the URL
    termsDialogVisible.value = true;
};

function editStatus() {
    isEditingStatus.value = true; // Switch to the Select component
    nextTick(() => {
        // Focus and open the dropdown
        if (selectRef.value) {
            selectRef.value.$el.focus(); // Focus the select element
            selectRef.value.$el.click(); // Open the dropdown
        }
    });
}

function stopEditing() {
    isEditingStatus.value = false; // Switch back to the Tag component
}

async function handleStatusChange(newStatus) {
    try {
        await UsersService.patch({ 'id': user.value.id, 'status': newStatus.value })
        console.log("Status changed to:", newStatus);
        toast.add({ severity: 'success', summary: 'Status updated', life: 3000 });
        user.value.status = newStatus.value;
        isEditingStatus.value = false;
    } catch (err) {
        toast.add({ severity: 'error', summary: 'Update failed', detail: 'Please refresh and try again', life: 3000 });
        console.error(err)
    }
    // Add any additional logic you need when the status changes
}

function validateChanges(data) {
    let validationErrors = [];
    //maybe we want to unset the link
    // if (!data.affiliateLink) {
    //     validationErrors.push('Add affiliate link to save');
    // }
    // if (!domainPattern.test(data.affiliateLink)) {
    //     validationErrors.push('Login is not in the correct format.');
    // }
    if (validationErrors.length) {
        let errorDetail = "";
        validationErrors.forEach((validationErr) => {
            errorDetail += validationErr + "\n";
        })
        console.log('validation failed');
        toast.add({ severity: 'error', summary: 'Validation failed', detail: errorDetail, life: 3000 });
        editingRows.value = [...editingRows.value, data];
        return false;
    } else
        return true;

}

const onUpdateSiteStatus = async (event) => {
    let { newValue, index, oldValue, data } = event;
    if (newValue !== oldValue) {
        try {
            const response = await SitesService.updateStatus(data.id, newValue);
            if (response.status === 200) {
                sites.value[index].status = newValue;
            }
        } catch (err) {
            console.error(err);
        }
    }
}



function onRowEditCancel(event) {
    console.log(event);
}

const anyAdvertisersBySiteId = (siteId) => {
    return computed(() => {
        let site = frozenSites.value.find(site => site.id === siteId);
        if (!site)
            site = sites.value.find(site => site.id === siteId);

        if (site.advertisers.length === 0) //last chance
        { return frozenAdvertisers.value[siteId] !== undefined; }

        if (showing.value === 'requested') {
            let advertisers = site.advertisers.filter(adv => adv.status === 'requested');
            if (advertisers.length === 0) {
                return frozenAdvertisers.value[siteId] !== undefined;
            }
            return true;
        }


        return true;
    });
}

const sortAdvertisersByLatestCreatedAt = (advertisers = []) => {
    return [...advertisers].sort((a, b) => {
        const aCreatedAt = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bCreatedAt = b?.createdAt ? new Date(b.createdAt).getTime() : 0;

        return bCreatedAt - aCreatedAt;
    });
};


const getVisibleAdvertisersForSite = (site) => {
    if (!site) {
        return [];
    }

    const baseAdvertisers = Array.isArray(site.advertisers) ? site.advertisers : [];
    const frozenAdvertisersForSite = getFrozenAdvertisers(site.id);
    const allAdvertisersForSite = [...baseAdvertisers, ...frozenAdvertisersForSite];

    const filteredAdvertisers = showing.value === 'requested'
        ? allAdvertisersForSite.filter(adv => adv.status === 'requested')
        : allAdvertisersForSite.filter(adv => adv.status !== 'deleted');

    const dedupedAdvertisers = filteredAdvertisers.filter((advertiser, index, arr) =>
        index === arr.findIndex(item => item.linkId === advertiser.linkId)
    );

    return sortAdvertisersByLatestCreatedAt(dedupedAdvertisers);
};

const allAdvertisers = computed(() => {
    const allSites = [...sites.value, ...frozenSites.value].filter(site => site && site.id);

    return sortAdvertisersByLatestCreatedAt(
        allSites.flatMap(site =>
            getVisibleAdvertisersForSite(site).map(advertiser => ({
                ...advertiser,
                siteName: site.name,
                siteUrl: site.url,
                siteCreatedAt: site.createdAt
            }))
        )
    );
});


//this doesnt perform great when there is a frozen row
const getAdvertisersBySiteId = (siteId) => {
    return computed(() => {
        try {
            let site = frozenSites.value.find(site => site.id === siteId);
            if (!site)
                site = sites.value.find(site => site.id === siteId);

            return getVisibleAdvertisersForSite(site);
        } catch (err) {
            console.error(err);
        }

    });
};


async function fetchSites() {
    try {
        sitesLoading.value = true;
        await SitesService.getByUserId(props.id).then((data) => {
            sites.value = data.data;
        });
        sitesLoading.value = false;
    } catch (err) {
        console.error(err);
    }
}

async function fetchSiteCount() {
    try {
        await SitesService.getTotalSitescount(props.id).then((data) => {
            userMetaData.value = data.data;
        });
    } catch (err) {
        console.error(err);
    }
}

async function fetchUserData() {
    try {
        const response = await UsersService.get(props.id);
        user.value = response.data;
        originalCommission.value = response.data.commission;
        console.log('User data fetched:', user.value);
    } catch (err) {
        console.error(err);
    }
}

const expandAll = () => {
    expandedRows.value = sites.value.reduce((acc, p) => (acc[p.id] = true) && acc, {});
};

const collapseAll = () => {
    expandedRows.value = null;
};

const getFrozenAdvertisers = (siteId) => {
    return frozenAdvertisers.value[siteId] || [];
}

function checkForFrozenRows() {
    if (route.query.showing !== undefined && route.query.showing === 'all') {
        showing.value = 'all';
    }
    if (route.query.siteId !== undefined) {
        let site = sites.value.find(site => site.id === route.query.siteId);
        if (site !== undefined) {
            let siteIndex = sites.value.findIndex(site => site.id === route.query.siteId);
            sites.value = sites.value.filter((c, i) => i !== siteIndex);
            frozenSites.value.push(site);

        }

        if (route.query.linkId !== undefined) {
            let advertiser = site.advertisers.find(adv => adv.linkId === route.query.linkId);
            if (advertiser !== undefined) {
                let advertiserIndex = site.advertisers.findIndex(adv => adv.linkId === route.query.linkId);
                site.advertisers = site.advertisers.filter((c, i) => i !== advertiserIndex);
                frozenAdvertisers.value[route.query.siteId] = [advertiser];
                // expandedRows.value[route.query.siteId] = true;
            }
        }
    }
}

// Create a map for quick lookups
const siteTypeMap = computed(() => {
    return getSiteTypes().reduce((acc, type) => {
        acc[type.code] = type.name;
        return acc;
    }, {});
});

const loginAsPublisherLoading = ref(false);
const loginAsPublisher = async () => {
    try {
        loginAsPublisherLoading.value = true;
        AuthService.loginAsPublisher(user.value.id).then(async (data) => {
            console.log(data);
            localStorage.setItem('token', data.data.token);
            await store.dispatch('setUser', data.data.mappedUser);
            // Remember which publisher page we came from, so "Back to Admin" returns here.
            try { localStorage.setItem('adminReturnUserId', user.value.id); } catch (e) { /* storage unavailable */ }
            router.push('/');
        }).catch((err) => {
            console.error(err);
        }).finally(() => {
            loginAsPublisherLoading.value = false;
        });

    } catch (err) {
        console.error(err);
    } finally {
        loginAsPublisherLoading.value = false;
    }
}

const addSiteRef = ref(null);

const handleAddSite = () => {
    addSiteRef.value.open();
};

const handleSiteAdded = (newSite) => {
    sites.value.unshift(newSite);
    // Add the new site ID to expanded rows
    expandedRows.value = {
        ...expandedRows.value,
        [newSite.id]: true
    };
};

const isUserSeo = computed(() => {
    return user.value?.userType?.toLowerCase() === 'seo';
});

const addDealRef = ref(null);
const selectedSite = ref(null);

const handleAddNewDeal = async (site) => {
    selectedSite.value = site;
    // Wait for next tick to ensure the value is set
    await nextTick();
    addDealRef.value.open();
};


const handleDealSaveAndAutomateLinkRequest = async (response) => {
    try {
        await AdvertiserService.requestAffiliateLink(
            props.id,
            response.advertiserId,
            selectedSite.value.id,
            selectedSite.value.country,
            response.newDeal.voonixAdvertiserId,
            response.newDeal.dealId,
            response.newDeal.isPublic,
            response.newDeal.version
        );

        // Ensure the parent site row is expanded
        expandedRows.value = {
            ...expandedRows.value,
            [selectedSite.value.id]: true
        };

        await fetchSites();

    } catch (err) {
        console.error(err);
    }
};

function editRow(index) {
    if (!editingRows.value.includes(index)) {
        editingRows.value.push(index);
    }
}

async function softDeleteSiteBrandLink(data, index) {
    try {
        const response = await SiteLinkService.softDeleteSiteBrandLink(data.linkId);
        if (response.status === 200) {
            toast.add({ severity: 'success', summary: 'Success', detail: 'Site brand link deleted successfully', life: 3000 });
            let siteId = data.siteId;
            // Set the advertiser status to 'deleted'
            let site = sites.value.find((site) => site.id === siteId);
            if (site) {
                let advertiser = site.advertisers.find((adv) => adv.linkId === data.linkId);
                if (advertiser) {
                    advertiser.status = 'deleted';
                }
            }
            
        } else {
            toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete site brand link', life: 3000 });
        }
    } catch (err) {
        console.error(err);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete site brand link', life: 3000 });
    }
}

async function saveEdit(data, index) {
    if (!isValidAffiliateLink(data.affiliateLink, data.internalNote, data.affiliateSystem.name)) {
        toast.add({ severity: 'error', summary: 'Validation failed', detail: 'Please enter a valid affiliate link.', life: 3000 });
        return;
    } else {
        console.log('VALID', data.affiliateLink, data.internalNote, data.affiliateSystem.name);
        // onRowEditSave is async: without await its promise is always truthy, so the row
        // left edit mode (showing the unsaved values) even when the save was refused.
        if (await onRowEditSave({ data, index, newData: data }))
            editingRows.value = editingRows.value.filter(i => i !== index);
     
    }

}

function cancelEdit(index) {
    // Cancel logic...
    editingRows.value = editingRows.value.filter(i => i !== index);
}

// Mirrors AdvertisersTable.vue's handleAffiliateLinkInput: on every keystroke
// in the Tracking-link input, run the row's affiliate URL through the
// per-system enricher. Result may auto-append the click-id param and/or
// auto-populate internalNote (e.g. MyAffiliates mediaItemId, ReferOn pubid,
// Softswiss visit_id, CellXpert utm_campaign). Silent no-op for systems
// with no enricher registered.
function handleAffiliateLinkInput(data) {
    if (!data?.affiliateSystem?.name) return;
    const context = {
        advertiserName: data.name,
        userName: user.value?.name,
        userCompanyName: user.value?.companyName,
        userType: user.value?.type,
        market: data.market,
    };
    const result = enrichAffiliateLink(data.affiliateLink, data.affiliateSystem.name, context);
    if (!result) return;
    if (result.affiliateLink && result.affiliateLink !== data.affiliateLink) {
        data.affiliateLink = result.affiliateLink;
    }
    if (result.internalNote && result.internalNote !== data.internalNote) {
        data.internalNote = result.internalNote;
    }
}

// ---------------------------------------------------------------------------
// Move a site-link to a different site (same publisher only).
//
// The link's siteId AND every matching Campaign.siteId move together — earnings
// reporting scopes on Campaign.siteId, so moving only the link would leave
// earnings attributed to the old site with no visible error. The backend does
// both writes; this is just the picker.
// ---------------------------------------------------------------------------
const linkContextMenu = ref(null);
const linkToMove = ref(null);
const showMoveDialog = ref(false);
const moveTargets = ref([]);
const moveTargetsLoading = ref(false);
const selectedMoveTargetId = ref(null);
const movingLink = ref(false);
const currentSiteNameForMove = ref('');

const linkContextMenuItems = ref([
    {
        label: 'Move to a different site',
        icon: 'pi pi-arrow-right-arrow-left',
        command: () => openMoveDialog(),
    },
]);

function onLinkRightClick(event) {
    event.originalEvent.preventDefault();
    linkToMove.value = event.data;
    linkContextMenu.value.show(event.originalEvent);
}

async function openMoveDialog() {
    if (!linkToMove.value?.linkId) return;
    showMoveDialog.value = true;
    selectedMoveTargetId.value = null;
    moveTargets.value = [];
    moveTargetsLoading.value = true;
    try {
        const response = await SiteLinkService.getMoveTargets(linkToMove.value.linkId);
        moveTargets.value = response.data?.sites || [];
        // Resolve a display name for the current site when the row doesn't carry one.
        const current = sites.value.find(s => s.id === response.data?.currentSiteId)
            || frozenSites.value.find(s => s.id === response.data?.currentSiteId);
        currentSiteNameForMove.value = current?.name || '';
    } catch (error) {
        console.error('Error loading move targets:', error);
        toast.add({
            severity: 'error', summary: 'Error',
            detail: error.response?.data?.message || 'Could not load available sites',
            life: 4000,
        });
    } finally {
        moveTargetsLoading.value = false;
    }
}

async function confirmMoveLink() {
    if (!linkToMove.value?.linkId || !selectedMoveTargetId.value) return;
    movingLink.value = true;
    try {
        const response = await SiteLinkService.moveToSite(linkToMove.value.linkId, selectedMoveTargetId.value);
        const moved = response.data?.campaignsMoved ?? 0;
        toast.add({
            severity: 'success', summary: 'Link moved',
            detail: moved > 0
                ? `Link moved. ${moved} campaign${moved === 1 ? '' : 's'} re-pointed to the new site.`
                : 'Link moved. No mapped campaigns to re-point.',
            life: 4000,
        });
        showMoveDialog.value = false;
        // Refresh so both tables reflect the new grouping.
        await fetchSites();
    } catch (error) {
        console.error('Error moving link:', error);
        toast.add({
            severity: 'error', summary: 'Move failed',
            detail: error.response?.data?.message || 'Could not move the link',
            life: 6000,
        });
    } finally {
        movingLink.value = false;
    }
}

// Same row helpers as /advertisers: open the per-system tracking-link docs and
// copy the params a tracking link needs.
function openHelpForSystem(system) {
    const url = router.resolve({ name: 'Help', query: helpQueryForSystem(system) }).href;
    window.open(url, '_blank', 'noopener,noreferrer');
}

async function copyLinkParams(system, internalNote = '') {
    const textToCopy = getLinkParamsText(system, internalNote);
    if (!textToCopy) {
        toast.add({ severity: 'warn', summary: 'Warning', detail: 'No link parameters available for this system', life: 3000 });
        return;
    }
    try {
        await navigator.clipboard.writeText(textToCopy);
        toast.add({ severity: 'success', summary: 'Success', detail: 'Link parameters copied to clipboard', life: 3000 });
    } catch (err) {
        console.error(err);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to copy to clipboard', life: 3000 });
    }
}

// Shown when a save is refused because the campaign key is taken on this login.
const showDuplicateKeyDialog = ref(false);
const duplicateKeyDetails = ref(null);

async function onRowEditSave(event) {
    const { data, index, newData } = event;

    if (validateChanges(newData)) {
        try {
            console.log('NEWDATA', newData);
            const dataUpdate = {
                internalNote: newData.internalNote,
                affiliateLink: newData.affiliateLink
            };
            const response = await SiteLinkService.updateSiteBrandLink(data.linkId, dataUpdate);
            if (response.status === 200) {
                let siteId = data.siteId;
                // Find the site in both `sites` and `frozenSites`, which could result in null if not found
                const siteInSites = sites.value.find((site) => site.id === siteId) || null;
                const siteInFrozenSites = frozenSites.value.find((frozenSite) => frozenSite.id === siteId) || null;

                // Initialize advertiser to null
                let advertiser = null;

                // Check if the site exists in `sites` and search for the advertiser
                if (siteInSites) {
                    advertiser = siteInSites.advertisers.find((adv) => adv.linkId === data.linkId);
                }

                // If the advertiser is not found in `sites`, check in `frozenSites`
                if (!advertiser && siteInFrozenSites) {
                    advertiser = siteInFrozenSites.advertisers.find((adv) => adv.linkId === data.linkId);
                }

                if (!advertiser) {
                    //last resort
                    advertiser = frozenAdvertisers.value[siteId].find((adv) => adv.linkId === data.linkId);
                }
                advertiser.status = response.data.status;
                advertiser.internalNote = response.data.internalNote;
                advertiser.affiliateLink = response.data.affiliateLink;

                let message = 'Details successfully added.';
                let toastObj = { severity: 'success', summary: message, life: 3000 }
                toast.add(toastObj);
            }

        } catch (err) {
            console.error(err);
            if (isCampaignKeyTaken(err)) {
                duplicateKeyDetails.value = err.response.data;
                showDuplicateKeyDialog.value = true;
            } else {
                toast.add({ severity: 'error', summary: 'Link not saved', detail: err.response?.data?.message || 'Failed to update link.', life: 6000 });
            }
            return false;
        }
        return true;
    }
}

const confirm = useConfirm();

const confirmDeleteSiteBrandLink = (event, data, index) => {
    confirm.require({
        target: event.currentTarget,
        message: 'Do you want to delete this site brand link?',
        icon: 'pi pi-info-circle',
        rejectProps: {
            label: 'Cancel',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Delete',
            severity: 'danger'
        },
        accept: async () => {
            await softDeleteSiteBrandLink(data, index);
        },
        reject: () => {
            // Optional: You can add a toast here if needed
        }
    });
};

onMounted(async () => {
    await Promise.all([fetchSites(), fetchUserData()]);
    fetchSiteCount();
    // expandAll();

    checkForFrozenRows();
    isLoading.value = false;
});
</script>

<style scoped>
.clickable-link {
    color: white;
    text-decoration: underline;
}

.p-breadcrumb {
    background: none !important;
    padding: 0 !important;
    margin-bottom: 8px !important;
}

.aff-link {
    color: rgb(213, 179, 40);
    text-decoration: underline;
}
</style>
